/**
 * Store Data Functions
 *
 * Fetches store/boutique data from Algolia for PlaceholderStores component.
 * Internal function used by API routes and the cached public function.
 */

import { unstable_cache } from 'next/cache';

import { BRAND, BRAND_ENUM } from '@/lib/cms/config';
import { devLog, devWarn } from '@/lib/utils/devLog';
import { getAlgoliaClient } from './client';
import { ALGOLIA_CONFIG } from './config';
import { algoliaContextFields, logAlgoliaRequest, logAlgoliaResponse } from './debugLog';

// ============================================
// Types
// ============================================

/**
 * Store hit from Algolia STORE index
 */
export interface StoreHit {
  objectID: string;
  store_id: string;
  name: string;
  name_en: string;
  city: string;
  city_en?: string;
  street: string;
  street_en?: string;
  street2?: string;
  street2_en?: string;
  region?: string;
  region_en?: string;
  postal_code?: string;
  phone: string;
  emails?: string[];
  type: 'BOUTIQUE' | 'FLAG_SHIP_BOUTIQUE' | 'OFFICIAL_RETAILER';
  operating_type?: string;
  country_code: string;
  slug: string;
  thumbnail_url: string;
  photo_gallery?: Array<{ image: { url: string } }>;
  focal_point?: { x: number; y: number };
  image_dimensions?: { x: number; y: number };
  hours?: Record<
    string,
    {
      isClosed?: boolean;
      openIntervals?: Array<{ start: string; end: string }>;
    }
  >;
  appointments?: Partial<
    Record<
      'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
      Array<{ startTime: string; endTime: string }>
    >
  >;
  temporary_hours?: {
    holiday_hours?: Array<{
      date: string;
      open_intervals: Array<{ start: string; end: string }> | null;
    }>;
    reopen_date?: string | null;
  };
  utc_offset?: string;
  is_highlighted: boolean;
  web_visible: boolean;
  /** Yext `c_galletAvailable` — Gallet storefront availability */
  gallet_available?: boolean;
  offers_appointment: boolean;
  offers_boutique_pickup?: boolean;
  service_your_watch?: boolean;
  description?: string;
  /** Geolocation for sorting by distance (Algolia may store reduced precision) */
  _geoloc?: { lat: number; lng: number };
  /** Full-precision latitude from Yext / POS (preferred for maps links) */
  latitude?: number;
  /** Full-precision longitude from Yext / POS (preferred for maps links) */
  longitude?: number;
  /** Legacy full-precision latitude (Breitling Algolia field) */
  lat?: number;
  /** Legacy full-precision longitude (Breitling Algolia field) */
  long?: number;
  /** Localized Algolia fields (e.g. name_de, city_fr) */
  [key: string]: unknown;
}

/**
 * Parameters for store fetch
 */
export interface StoreSearchParams {
  /** Country code (e.g., 'US', 'GB') */
  countryCode: string;
  /** Maximum number of stores to return */
  limit?: number;
  /** Type of placeholder (affects which stores to prioritize) */
  mode?: 'Default' | 'Search';
  /** Optional search query for text search */
  query?: string;
  /** Optional store type filter */
  storeType?: string[];
  /**
   * Curated boutique store_id list from CMS `boutiqueReferenceKeys`. When
   * present, restricts the result set to exactly these IDs (legacy "Manual"
   * mode) and bypasses country/type filtering.
   */
  storeIds?: string[];
  /**
   * Restrict to boutiques that stock Breitling eyewear (sourced from
   * PlaceholderStores.eyewearAvailable in CMS). Adds the
   * `eyewear_available:true` Algolia filter.
   */
  eyewearAvailable?: boolean;
  /**
   * Restrict to boutiques available on the Gallet storefront (Yext
   * `c_galletAvailable`). Adds the `gallet_available:true` Algolia filter.
   */
  galletAvailable?: boolean;
  /** Geo sort: `"lat,lng"` string for proximity ordering */
  aroundLatLng?: string;
  /** Geo radius in meters, or `"all"` for unlimited */
  aroundRadius?: number | 'all';
  /** Zero-based page number for pagination */
  page?: number;
  /** Restrict to stores that offer boutique pickup */
  boutiquePickup?: boolean;
}

/** Store type facet counts */
export type StoreTypeFacets = Record<string, number>;

/**
 * Result from store fetch
 */
export interface StoreSearchResult {
  stores: StoreHit[];
  totalHits: number;
  /** Facet counts for store type */
  facets?: {
    type?: StoreTypeFacets;
  };
}

export type StoreFilterParams = Pick<
  StoreSearchParams,
  | 'countryCode'
  | 'mode'
  | 'storeType'
  | 'storeIds'
  | 'eyewearAvailable'
  | 'galletAvailable'
  | 'boutiquePickup'
>;

/**
 * Whether Gallet storefront availability should constrain store queries.
 *
 * Example Input:
 * galletAvailable=undefined, BRAND="gallet"
 *
 * Example Output:
 * true
 */
export function shouldApplyGalletAvailableFilter(galletAvailable?: boolean): boolean {
  if (galletAvailable === false) {
    return false;
  }

  return galletAvailable === true || BRAND === BRAND_ENUM.Gallet;
}

/**
 * Build Algolia filter string for store search queries.
 *
 * Example Input:
 * { countryCode: "US", mode: "Default", galletAvailable: true }
 *
 * Example Output:
 * "country_code:US AND web_visible:true AND (type:FLAG_SHIP_BOUTIQUE OR type:BOUTIQUE) AND gallet_available:true"
 */
export function buildStoreFilters(params: StoreFilterParams): string {
  const {
    countryCode,
    mode = 'Default',
    storeType,
    storeIds,
    eyewearAvailable,
    galletAvailable,
    boutiquePickup,
  } = params;

  let filters: string;
  if (storeIds && storeIds.length > 0) {
    const storeIdFilters = storeIds.map((id) => 'store_id:' + id).join(' OR ');
    filters = '(' + storeIdFilters + ') AND web_visible:true';
  } else {
    const baseFilters =
      mode === 'Search'
        ? 'web_visible:true'
        : `country_code:${countryCode.toUpperCase()} AND web_visible:true`;

    if (storeType && storeType.length > 0) {
      const typeFilters = storeType.map((type) => 'type:' + type).join(' OR ');
      const typeFilter = '(' + typeFilters + ')';
      filters = `${baseFilters} AND ${typeFilter}`;
    } else if (mode === 'Default') {
      filters = `${baseFilters} AND (type:FLAG_SHIP_BOUTIQUE OR type:BOUTIQUE)`;
    } else {
      filters = baseFilters;
    }
  }

  if (eyewearAvailable) {
    filters = `${filters} AND eyewear_available:true`;
  }

  if (shouldApplyGalletAvailableFilter(galletAvailable)) {
    filters = `${filters} AND gallet_available:true`;
  }

  if (boutiquePickup) {
    filters = `${filters} AND offers_boutique_pickup:true`;
  }

  return filters;
}

// ============================================
// Internal Functions (API routes only)
// ============================================

/**
 * INTERNAL: Fetch stores directly from Algolia.
 * DO NOT USE outside of API routes.
 *
 * @internal
 */
export async function fetchStoresInternal(params: StoreSearchParams): Promise<StoreSearchResult> {
  const {
    countryCode,
    limit = 3,
    mode = 'Default',
    query = '',
    storeType,
    storeIds,
    eyewearAvailable,
    galletAvailable,
    aroundLatLng,
    aroundRadius,
    page = 0,
    boutiquePickup,
  } = params;

  const client = getAlgoliaClient({ ssr: true });
  const indexName = ALGOLIA_CONFIG.getStoreIndex();

  try {
    const filters = buildStoreFilters({
      countryCode,
      mode,
      storeType,
      storeIds,
      eyewearAvailable,
      galletAvailable,
      boutiquePickup,
    });

    logAlgoliaRequest('fetchStoresInternal', {
      indexName,
      environment: ALGOLIA_CONFIG.environment,
      countryCode,
      mode,
      query,
      filters,
      hitsPerPage: limit,
      page,
      storeType,
      storeIds,
      eyewearAvailable,
      galletAvailable,
      aroundLatLng,
      aroundRadius,
      boutiquePickup,
    });

    const response = await client.search<StoreHit>({
      requests: [
        {
          indexName,
          query,
          filters,
          hitsPerPage: limit,
          page,
          facets: ['type'],
          ...(aroundLatLng ? { aroundLatLng, aroundRadius: aroundRadius ?? 'all' } : {}),
        },
      ],
    });

    const result = response.results[0];

    if (!('hits' in result)) {
      devWarn('[fetchStoresInternal] Unexpected response format');
      return { stores: [], totalHits: 0 };
    }

    logAlgoliaResponse('fetchStoresInternal', {
      indexName,
      environment: ALGOLIA_CONFIG.environment,
      totalHits: result.nbHits ?? 0,
      hitsReturned: result.hits.length,
      processingTimeMs: 'processingTimeMS' in result ? (result.processingTimeMS ?? 0) : 0,
      sampleStoreIds: result.hits.slice(0, 5).map((store) => store.store_id),
      facetTypes: result.facets?.type ?? null,
    });

    // Sort client-side for proper priority
    // 1. FLAG_SHIP_BOUTIQUE + is_highlighted
    // 2. BOUTIQUE + is_highlighted
    // 3. FLAG_SHIP_BOUTIQUE
    // 4. BOUTIQUE
    const sortedHits = [...result.hits].sort((a, b) => {
      const aScore = getStorePriority(a);
      const bScore = getStorePriority(b);
      return bScore - aScore;
    });

    return {
      stores: sortedHits.slice(0, limit),
      totalHits: result.nbHits ?? 0,
      facets: {
        type: result.facets?.type as StoreTypeFacets | undefined,
      },
    };
  } catch (error) {
    devWarn('[fetchStoresInternal] Error:', error);
    return { stores: [], totalHits: 0 };
  }
}

/**
 * Get priority score for sorting stores
 */
function getStorePriority(store: StoreHit): number {
  let score = 0;

  if (store.type === 'FLAG_SHIP_BOUTIQUE') score += 100;
  else if (store.type === 'BOUTIQUE') score += 50;

  if (store.is_highlighted) score += 25;

  return score;
}

// ============================================
// Standard Functions (cached direct call)
// ============================================

const STORES_REVALIDATE = 3600; // 1 hour, matching previous ISR config

const cachedFetchStores = unstable_cache(
  async (
    countryCode: string,
    limit: number,
    mode: 'Default' | 'Search',
    storeIdsKey: string,
    eyewearAvailable: boolean,
    galletAvailable: boolean
  ) => {
    const storeIds = storeIdsKey ? storeIdsKey.split(',') : undefined;
    const result = await fetchStoresInternal({
      countryCode,
      limit,
      mode,
      storeIds,
      eyewearAvailable,
      galletAvailable,
    });
    return result.stores;
  },
  ['algolia-stores-v3'],
  // Tags are not currently wired to any revalidation webhook — they exist so
  // we can call revalidateTag('algolia') programmatically if needed in the future.
  { revalidate: STORES_REVALIDATE, tags: ['algolia', 'algolia-stores'] }
);

/**
 * Fetch stores for PlaceholderStores component.
 *
 * Cached for 1 hour via unstable_cache.
 * Calls the internal Algolia function directly (no HTTP round-trip).
 *
 * @param params - Search parameters
 * @returns Array of store hits
 */
/**
 * Fetch a single boutique by slug (globally unique in Algolia).
 *
 * Example Input:
 * slug = "gallet-boutique-san-francisco"
 *
 * Example Output:
 * StoreHit | null
 */
export const SAFE_SLUG = /^[a-z0-9-]+$/;

/**
 * Allowed shape for a short-link route's `storeId` param, before normalization
 * (used by the stores detail/appointment redirect pages).
 */
export const SHORT_LINK_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export async function fetchStoreBySlug(slug: string): Promise<StoreHit | null> {
  const normalizedSlug = slug.trim().toLowerCase();

  if (!normalizedSlug || !SAFE_SLUG.test(normalizedSlug)) {
    return null;
  }

  try {
    const client = getAlgoliaClient({ ssr: true });
    const indexName = ALGOLIA_CONFIG.getStoreIndex();
    const galletFilter = BRAND === BRAND_ENUM.Gallet ? ' AND gallet_available:true' : '';
    const filters = `slug:"${normalizedSlug}" AND web_visible:true AND (type:FLAG_SHIP_BOUTIQUE OR type:BOUTIQUE)${galletFilter}`;

    const response = await client.search<StoreHit>({
      requests: [
        {
          indexName,
          query: '',
          filters,
          hitsPerPage: 1,
        },
      ],
    });

    const result = response.results[0];
    if (!('hits' in result) || result.hits.length === 0) {
      return null;
    }

    return result.hits[0] ?? null;
  } catch (error) {
    devWarn('[fetchStoreBySlug] Error:', error);
    return null;
  }
}

/**
 * Fetch a single boutique by store_id (Algolia STORE index `store_id`;
 * externally known as the boutique Entity ID / legacy trackingKey).
 *
 * Example Input:
 * storeId = "CH208104"
 *
 * Example Output:
 * StoreHit | null
 */
const NORMALIZED_STORE_ID_PATTERN = /^[A-Z0-9_-]+$/;

export async function fetchStoreByStoreId(storeId: string): Promise<StoreHit | null> {
  const normalizedStoreId = storeId.trim().toUpperCase();

  if (!normalizedStoreId || !NORMALIZED_STORE_ID_PATTERN.test(normalizedStoreId)) {
    return null;
  }

  try {
    const client = getAlgoliaClient({ ssr: true });
    const indexName = ALGOLIA_CONFIG.getStoreIndex();
    const galletFilter = BRAND === BRAND_ENUM.Gallet ? ' AND gallet_available:true' : '';
    const filters = `store_id:"${normalizedStoreId}" AND web_visible:true AND (type:FLAG_SHIP_BOUTIQUE OR type:BOUTIQUE)${galletFilter}`;

    const response = await client.search<StoreHit>({
      requests: [
        {
          indexName,
          query: '',
          filters,
          hitsPerPage: 1,
        },
      ],
    });

    const result = response.results[0];
    if (!('hits' in result) || result.hits.length === 0) {
      return null;
    }

    return result.hits[0] ?? null;
  } catch (error) {
    devWarn('[fetchStoreByStoreId] Error:', error);
    return null;
  }
}

export async function fetchStores(params: StoreSearchParams): Promise<StoreHit[]> {
  const {
    countryCode,
    limit = 3,
    mode = 'Default',
    storeIds,
    eyewearAvailable,
    galletAvailable,
  } = params;

  devLog('[fetchStores] Request:', {
    countryCode,
    limit,
    mode,
    storeIds,
    eyewearAvailable,
    galletAvailable,
  });

  try {
    // unstable_cache keys must be primitives — encode the curated id list as
    // a sorted comma-joined string so the cache distinguishes different sets.
    const storeIdsKey = storeIds && storeIds.length > 0 ? [...storeIds].sort().join(',') : '';
    const stores = await cachedFetchStores(
      countryCode,
      limit,
      mode,
      storeIdsKey,
      Boolean(eyewearAvailable),
      Boolean(galletAvailable)
    );

    devLog('[fetchStores] Result:', { count: stores?.length || 0 });

    return stores || [];
  } catch (error) {
    devWarn('[fetchStores] Error:', error);
    return [];
  }
}
